import { formatDateTime, formatNumber, toMachineDate } from '@shared/format';
import { DTable, DTableBody, DTableHead, DTableRowHeader, DTableScroll } from '@shared/ui/dtable';

import type { ExternalBinding } from '../_shared/types';
import { copy } from './copy';

/**
 * The provider binding, its scores, and the re-fetch that refreshes them.
 *
 * **The ratings block is a repeating structure that ships one row**, and the
 * plurality is a data condition rather than a design element: the configuration
 * binds exactly one provider today, and the note under the table says a second
 * binding produces a second row through the same markup. Naming a second vendor
 * here would invent a binding the catalog has not made.
 *
 * **The reference link is a fact, not a field.** Both public surfaces build it
 * from the identifier above it and print that identifier as the link's own text,
 * so what the operator reads here is what the viewer reads there — and a
 * hand-editable URL would open two sources of truth for one value with no way to
 * tell which is lying. Correcting a wrong match is a re-bind by identifier, not
 * an edit of link text.
 *
 * **The action sits on the heading row**, like every other section action on
 * this page: a lone button under a box reads as a submit for the box it closes.
 */
export function ProviderCard({
  binding,
  busy,
  onRefetch,
}: {
  binding: ExternalBinding | null;
  busy: boolean;
  onRefetch: () => void;
}) {
  return (
    <section
      aria-labelledby="edit-provider"
      className="grid gap-3 rounded-md border border-border bg-surface p-4"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <h2 id="edit-provider" className="font-display text-h3 font-bold">
          {copy.provider.heading}
        </h2>

        <button
          type="button"
          disabled={busy || binding === null}
          onClick={onRefetch}
          className="hit-target ms-auto rounded-sm border border-border-strong bg-surface-raised px-3 text-xs font-semibold text-ink hover:bg-surface-overlay disabled:cursor-not-allowed disabled:opacity-45"
        >
          {busy ? copy.provider.refetching : copy.provider.refetch}
        </button>
      </div>

      {binding === null ? (
        <p className="mb-0 max-w-none text-sm text-ink-muted">{copy.provider.unbound}</p>
      ) : (
        <>
          <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-2 text-sm">
            <dt className="text-ink-muted">{copy.provider.provider}</dt>
            <dd className="mb-0">
              <bdi className="mono">{binding.provider}</bdi>
            </dd>

            <dt className="text-ink-muted">{copy.provider.externalId}</dt>
            <dd className="mb-0">
              <bdi className="mono">{binding.external_id}</bdi>
            </dd>

            <dt className="text-ink-muted">{copy.provider.lastFetched}</dt>
            <dd className="mb-0">
              {binding.last_fetched_at ? (
                <time dateTime={toMachineDate(binding.last_fetched_at)}>
                  <bdi>{formatDateTime(binding.last_fetched_at)}</bdi>
                </time>
              ) : (
                copy.state.never
              )}
            </dd>
          </dl>

          {binding.ratings.length > 0 ? (
            <>
              <DTableScroll>
                <DTable caption={copy.provider.ratingsCaption} minInlineSize="18rem">
                  <DTableHead>
                    <tr>
                      <th scope="col">{copy.provider.ratingColumns.source}</th>
                      <th scope="col">{copy.provider.ratingColumns.score}</th>
                      <th scope="col">{copy.provider.ratingColumns.votes}</th>
                    </tr>
                  </DTableHead>
                  <DTableBody>
                    {binding.ratings.map((rating) => (
                      <tr key={rating.source}>
                        <DTableRowHeader>
                          <bdi className="mono">{rating.source}</bdi>
                        </DTableRowHeader>
                        <td className="whitespace-nowrap">
                          <bdi>{formatNumber(rating.score)}</bdi> /{' '}
                          <bdi>{formatNumber(rating.score_max)}</bdi>
                        </td>
                        <td className="whitespace-nowrap">
                          <bdi>{formatNumber(rating.vote_count)}</bdi>
                        </td>
                      </tr>
                    ))}
                  </DTableBody>
                </DTable>
              </DTableScroll>

              <p className="mb-0 max-w-none text-xs text-ink-muted">{copy.provider.ratingsNote}</p>
            </>
          ) : null}

          <dl className="grid gap-1 text-sm">
            <dt className="text-ink-muted">{copy.provider.referenceLabel}</dt>
            <dd className="mb-0">
              <a
                href={binding.reference_url}
                rel="noopener noreferrer nofollow"
                target="_blank"
                className="text-accent-text"
              >
                {copy.provider.referenceText(binding.provider, binding.external_id)}
              </a>
            </dd>
          </dl>

          <p className="mb-0 max-w-none text-xs text-ink-muted">{copy.provider.referenceNote}</p>
        </>
      )}
    </section>
  );
}
